import { InviteRole, PendingInvite } from './types';

const EXISTING_ORG_MEMBER_ROLES = new Set([
  'member',
  'collaborator',
  'manager',
  'billing',
  'billing_manager'
]);

export const parseEmailInput = (value?: string | null): string[] =>
  (value ?? '')
    .split(/[,;\s]+/)
    .map(email => email.trim())
    .filter(Boolean);

export const normalizeEmail = (value?: string | null): string =>
  (value ?? '').trim().toLowerCase();

export const remainingSeats = (
  membersMax: number | null | undefined,
  membersCount: number
): number => {
  if (membersMax == null) return Infinity;
  return Math.max(0, membersMax - membersCount);
};

export const usesMemberSeat = (invite: PendingInvite): boolean => {
  if (invite.role !== 'member') return false;
  if (EXISTING_ORG_MEMBER_ROLES.has(invite.existingOrgRole ?? '')) {
    return false;
  }
  return true;
};

export const newMemberSeatsUsed = (invites: PendingInvite[]): number =>
  invites.filter(usesMemberSeat).length;

export const isExistingOrgMemberOrCollaborator = (
  existingOrgRole?: string
): boolean => EXISTING_ORG_MEMBER_ROLES.has(existingOrgRole ?? '');

export const isMemberOptionDisabled = (
  pendingInvites: PendingInvite[],
  membersMax: number | null | undefined,
  membersCount: number,
  existingOrgRole?: string
): boolean => {
  if (isExistingOrgMemberOrCollaborator(existingOrgRole)) return false;

  return (
    newMemberSeatsUsed(pendingInvites) >=
    remainingSeats(membersMax, membersCount)
  );
};

export const normalizeInviteRoleProp = (
  role?: string | null
): InviteRole | undefined => {
  if (role === 'client' || role === 'guest') return 'guest';
  if (role === 'collaborator') return 'collaborator';
  if (role === 'member') return 'member';
  return undefined;
};

export const roleForNewOrgInvite = (
  roleFromProps: string | null | undefined,
  project: boolean,
  pendingInvites: PendingInvite[],
  membersMax: number | null | undefined,
  membersCount: number
): InviteRole => {
  const intended =
    normalizeInviteRoleProp(roleFromProps) ?? (project ? 'guest' : 'member');

  if (
    intended === 'member' &&
    isMemberOptionDisabled(pendingInvites, membersMax, membersCount)
  ) {
    if (project) return 'guest';
    return 'collaborator';
  }

  return intended;
};

export const roleLabel = (role: InviteRole, project?: boolean): string => {
  if (role === 'guest') return project ? 'Client' : 'Guest';
  if (role === 'collaborator') return 'Collaborator';
  return 'Member';
};

export const groupInvitesByRole = (
  invites: PendingInvite[]
): Map<InviteRole, PendingInvite[]> => {
  const groups = new Map<InviteRole, PendingInvite[]>();
  invites.forEach(invite => {
    const group = groups.get(invite.role) ?? [];
    group.push(invite);
    groups.set(invite.role, group);
  });
  return groups;
};

export const canUseMemberRole = (
  invite: Pick<PendingInvite, 'existingOrgRole'>,
  pendingInvites: PendingInvite[],
  membersMax: number | null | undefined,
  membersCount: number,
  excludeKey?: string
): boolean => {
  if (EXISTING_ORG_MEMBER_ROLES.has(invite.existingOrgRole ?? '')) {
    return true;
  }

  const seatsUsed = newMemberSeatsUsed(
    pendingInvites.filter(item => item.key !== excludeKey)
  );

  return seatsUsed < remainingSeats(membersMax, membersCount);
};
